import React from 'react';
import { motion } from 'framer-motion';
import { Container } from '../ui/Container';
import { Link } from 'react-router-dom';
import { 
  Microscope, 
  Brain, 
  Apple, 
  Pill, 
  Activity, 
  Share2, 
  Watch 
} from 'lucide-react';
import ecosystemRender from '../../assets/pitch-deck/7-pillars-ecosystem.png';

const pillars = [
  { id: 'lab', title: 'Lab OS', icon: Microscope, color: 'text-blue-500', bg: 'bg-blue-500/10', desc: 'Standing monthly clinical-grade panels.', path: '/ai-lab' },
  { id: 'ai', title: 'AI OS', icon: Brain, color: 'text-purple-500', bg: 'bg-purple-500/10', desc: 'Synthesizing data into actionable scores.', path: '/ai-lab' },
  { id: 'food', title: 'Food OS', icon: Apple, color: 'text-emerald-500', bg: 'bg-emerald-500/10', desc: 'Artisanal organic & vegan nutrition protocols.', path: '/ai-lab' },
  { id: 'supp', title: 'Supplement OS', icon: Pill, color: 'text-amber-500', bg: 'bg-amber-500/10', desc: 'Custom formulations mapped to labs.', path: '/ai-lab' },
  { id: 'move', title: 'Movement OS', icon: Activity, color: 'text-orange-500', bg: 'bg-orange-500/10', desc: 'Bio-adaptive exercise and yoga.', path: '/ai-lab' },
  { id: 'social', title: 'Social OS', icon: Share2, color: 'text-indigo-500', bg: 'bg-indigo-500/10', desc: 'Verified community sharing of health milestones.', path: '/ai-lab' },
  { id: 'wear', title: 'Wearable OS', icon: Watch, color: 'text-cyan-500', bg: 'bg-cyan-500/10', desc: 'Real-time performance & meal sync.', path: '/ai-lab' },
];

const SevenPillarsSection: React.FC = () => {
  return (
    <section className="py-32 bg-theme-bg relative border-y border-theme-border">
      <Container>
        <div className="text-center mb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-theme-primary/10 border border-theme-primary/20 text-theme-primary text-xs font-bold uppercase tracking-widest mb-6"
          >
            <span>Platform Defensibility</span>
          </motion.div>
          <h2 className="text-5xl md:text-6xl font-black text-theme-text-primary mb-6 leading-tight">
            The 7 Pillars of <br /> <span className="gradient-heading">Health OS.</span>
          </h2>
          <p className="text-xl text-theme-text-secondary max-w-2xl mx-auto font-light">
            BIOTUNE isn't a feature; it's a vertical integration of every health vertical into a single, cohesive operating system.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: The 3D Render */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="relative"
          >
            <div className="relative z-10 p-4 bg-theme-surface rounded-[64px] border border-theme-border backdrop-blur-xl shadow-2xl">
              <img 
                src={ecosystemRender} 
                alt="The 7 Pillars Ecosystem"
                className="w-full h-auto rounded-[48px] shadow-3xl"
              />
            </div>
            {/* Decorative background glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-theme-primary/5 blur-[150px] -z-10" />
          </motion.div>

          {/* Right: The Grid of Pillar */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {pillars.map((pillar, idx) => (
              <motion.div
                key={pillar.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="relative"
              >
                <Link 
                  to={pillar.path}
                  className="block p-6 rounded-3xl bg-theme-surface border border-theme-border hover:border-theme-primary/30 hover:bg-theme-elevated transition-all group shadow-sm hover:shadow-lg h-full"
                >
                  <div className={`w-12 h-12 rounded-2xl ${pillar.bg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <pillar.icon className={`w-6 h-6 ${pillar.color}`} />
                  </div>
                  <h4 className="text-lg font-bold text-theme-text-primary mb-2">{pillar.title}</h4>
                  <p className="text-sm text-theme-text-secondary leading-relaxed">{pillar.desc}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
};

export default SevenPillarsSection;
